import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { useContract } from '@/hooks/useContract';
import { useToast } from '@/contexts/ToastContext';
import { CharacterClass, CLASS_INFO } from '@/lib/types/game';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { ClassSprite } from '@/components/game/PixelSprites';

// Retro pixel-art style class icons (SVG inline)
const ClassIcon: React.FC<{ classType: CharacterClass; size?: number }> = ({ classType, size = 20 }) => {
  const icons: Record<CharacterClass, React.ReactElement> = {
    [CharacterClass.FIGHTER]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Sword */}
        <rect x="7" y="1" width="2" height="10" fill="#C0C0C0" />
        <rect x="5" y="10" width="6" height="2" fill="#8B4513" />
        <rect x="6" y="12" width="4" height="2" fill="#8B4513" />
        <rect x="7" y="0" width="2" height="2" fill="#FFD700" />
      </svg>
    ),
    [CharacterClass.THIEF]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Dagger */}
        <rect x="7" y="2" width="2" height="8" fill="#C0C0C0" />
        <rect x="5" y="9" width="6" height="1" fill="#8B4513" />
        <rect x="6" y="10" width="4" height="3" fill="#4B0082" />
        <rect x="7" y="1" width="2" height="2" fill="#C0C0C0" />
      </svg>
    ),
    [CharacterClass.MONK]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Fist */}
        <rect x="4" y="4" width="8" height="8" fill="#FFDBAC" />
        <rect x="4" y="5" width="2" height="6" fill="#FFDBAC" />
        <rect x="10" y="5" width="2" height="6" fill="#FFDBAC" />
        <rect x="3" y="6" width="2" height="4" fill="#FFDBAC" />
        <rect x="5" y="12" width="6" height="2" fill="#FFDBAC" />
      </svg>
    ),
    [CharacterClass.RED_MAGE]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Hat with feather */}
        <rect x="4" y="6" width="8" height="6" fill="#DC143C" />
        <rect x="5" y="4" width="6" height="3" fill="#DC143C" />
        <rect x="6" y="2" width="4" height="3" fill="#DC143C" />
        <rect x="10" y="1" width="2" height="4" fill="#FFF" />
        <rect x="4" y="11" width="8" height="2" fill="#FFD700" />
      </svg>
    ),
    [CharacterClass.WHITE_MAGE]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Staff with cross */}
        <rect x="7" y="2" width="2" height="12" fill="#8B4513" />
        <rect x="5" y="2" width="6" height="2" fill="#87CEEB" />
        <rect x="6" y="1" width="4" height="4" fill="#87CEEB" />
        <rect x="7" y="0" width="2" height="2" fill="#FFF" />
      </svg>
    ),
    [CharacterClass.BLACK_MAGE]: (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
        {/* Staff with orb */}
        <rect x="7" y="4" width="2" height="10" fill="#8B4513" />
        <rect x="5" y="1" width="6" height="4" fill="#FF4500" />
        <rect x="6" y="2" width="4" height="2" fill="#FFD700" />
        <rect x="4" y="3" width="8" height="1" fill="#191970" />
      </svg>
    ),
  };

  return icons[classType];
};

interface SlotInfo {
  slots: boolean[];
  current: number;
}

const NewGame: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isConnected } = useWallet();
  const { createGame, getSlotInfo } = useContract();
  const toast = useToast();

  // Get target slot from navigation state (if coming from slot manager)
  const targetSlot = (location.state as { targetSlot?: number } | null)?.targetSlot;

  const [slotInfo, setSlotInfo] = useState<SlotInfo | null>(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(true);
  const [selectedClasses, setSelectedClasses] = useState<(CharacterClass | null)[]>([null, null, null, null]);
  const [isCreating, setIsCreating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Redirect if not connected
  useEffect(() => {
    if (!isConnected) {
      navigate('/');
    }
  }, [isConnected, navigate]);

  // Load slot info
  useEffect(() => {
    const loadSlots = async () => {
      setIsLoadingSlots(true);
      try {
        const info = await getSlotInfo();
        setSlotInfo(info);
      } catch (error) {
        console.error('Failed to load slots:', error);
      } finally {
        setIsLoadingSlots(false);
      }
    };

    if (isConnected) {
      loadSlots();
    }
  }, [isConnected, getSlotInfo]);

  // Get the slot to use - either the target slot from navigation or first available
  const getSlotToUse = (): number | null => {
    // If a target slot was specified from the slot manager, use it
    if (targetSlot !== undefined && targetSlot >= 0 && targetSlot < 8) {
      // Verify the slot is actually empty (it should be if coming from overwrite flow)
      if (!slotInfo?.slots[targetSlot]) {
        return targetSlot;
      }
    }
    // Otherwise find first available slot
    if (!slotInfo) return 0; // Default to slot 0 if no info yet
    const index = slotInfo.slots.findIndex(active => !active);
    return index === -1 ? null : index;
  };

  const handleClassSelect = (index: number, classType: CharacterClass) => {
    const newClasses = [...selectedClasses];
    newClasses[index] = classType;
    setSelectedClasses(newClasses);
  };

  const canCreateGame = selectedClasses.every((c) => c !== null);
  const slotToUse = getSlotToUse();
  const allSlotsFull = slotToUse === null;

  const handleCreateGame = async () => {
    if (!canCreateGame || slotToUse === null) {
      toast.error('Please select a class for all 4 characters');
      return;
    }

    setIsCreating(true);
    setShowConfirm(false);

    try {
      const classes = selectedClasses as [CharacterClass, CharacterClass, CharacterClass, CharacterClass];
      const result = await createGame(slotToUse, classes);

      if (result.success) {
        toast.success('Your adventure begins!');
        navigate('/game');
      } else {
        toast.error(result.error || 'Failed to create game. Please try again.');
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Failed to create game:', error);
      toast.error('Failed to create game. Please try again.');
      setIsCreating(false);
    }
  };

  const classOptions = [
    { class: CharacterClass.FIGHTER, color: 'bg-red-600' },
    { class: CharacterClass.THIEF, color: 'bg-purple-600' },
    { class: CharacterClass.MONK, color: 'bg-orange-600' },
    { class: CharacterClass.RED_MAGE, color: 'bg-pink-600' },
    { class: CharacterClass.WHITE_MAGE, color: 'bg-blue-400' },
    { class: CharacterClass.BLACK_MAGE, color: 'bg-indigo-700' },
  ];

  if (isLoadingSlots) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-[#1a1a2e] to-[#0f0e17] flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  // Show error if all slots are full
  if (allSlotsFull) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-[#1a1a2e] to-[#0f0e17] p-4">
        <div className="max-w-2xl mx-auto py-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-pixel-lg text-[#e94560] text-shadow">New Game</h1>
            <Button variant="secondary" onClick={() => navigate('/')}>
              Back
            </Button>
          </div>

          <Card className="bg-red-900/20 border-red-500">
            <div className="text-center py-8">
              <p className="text-game text-xl text-red-400 mb-4">
                All Save Slots Full
              </p>
              <p className="text-game text-sm text-gray-400 mb-6">
                You have 8 active save files. Delete an existing save from the Load Game menu to create a new adventure.
              </p>
              <Button variant="secondary" onClick={() => navigate('/')}>
                Return to Title
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Party Creation
  return (
    <div className="h-screen w-full bg-gradient-to-b from-[#1a1a2e] to-[#0f0e17] flex flex-col overflow-hidden">
      {/* Fixed Header with actions */}
      <header className="bg-[#0f3460] border-b-2 border-[#533483] px-4 py-3 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
              ← Back
            </Button>
            <h1 className="text-pixel-md text-[#e94560] text-shadow">Create Your Party</h1>
            {slotToUse !== null && (
              <span className="text-game text-xs text-gray-400 bg-[#1a1a2e] px-2 py-1 rounded">
                Slot {slotToUse + 1}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setSelectedClasses([null, null, null, null])}
              disabled={isCreating || !selectedClasses.some(c => c !== null)}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={() => setShowConfirm(true)}
              disabled={!canCreateGame}
              isLoading={isCreating}
            >
              {isCreating ? 'Creating...' : 'Begin Adventure →'}
            </Button>
          </div>
        </div>
      </header>

      {/* Main content area - scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-7xl mx-auto pt-4">
          {/* Party Preview - extra large sprites */}
          <div className="ff-border bg-[#0f3460] p-8 py-20 rounded mb-8">
            <div className="flex items-center justify-center gap-12 xl:gap-16">
              {selectedClasses.map((classType, index) => (
                <div key={index} className="flex flex-col items-center group">
                  <div className="text-game text-sm text-[#533483] mb-2 group-hover:text-[#e94560] transition-colors">
                    {index + 1}
                  </div>
                  <div className={`w-48 h-48 xl:w-56 xl:h-56 flex items-center justify-center mb-3 rounded-lg transition-all ${classType === null ? 'bg-[#1a1a2e]/50' : ''}`}>
                    {classType !== null ? (
                      <ClassSprite classId={classType} size={192} />
                    ) : (
                      <div className="w-44 h-44 xl:w-52 xl:h-52 border-4 border-dashed border-[#533483]/60 rounded-lg flex items-center justify-center hover:border-[#533483] transition-colors">
                        <span className="text-game text-5xl text-[#533483]/60">?</span>
                      </div>
                    )}
                  </div>
                  <span className={`text-game text-lg xl:text-xl transition-colors ${classType !== null ? 'text-white' : 'text-gray-500'}`}>
                    {classType !== null ? CLASS_INFO[classType].name : 'Select...'}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-2 mt-8">
              <span className="text-game text-sm text-gray-600">Tip:</span>
              <span className="text-game text-sm text-gray-400">Classic party is Fighter, Thief, White Mage, Black Mage</span>
            </div>
          </div>

          {/* Party Selection Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {selectedClasses.map((selectedClass, index) => {
            // Get classes already selected by OTHER slots (not this one)
            const otherSelectedClasses = selectedClasses
              .filter((_, i) => i !== index)
              .filter((c): c is CharacterClass => c !== null);

            const hasSelection = selectedClass !== null;

            return (
              <div key={index} className={`ff-border bg-[#16213e] rounded-lg overflow-hidden transition-all ${hasSelection ? 'ring-2 ring-[#533483]/50' : ''}`}>
                <div className={`px-5 py-3 border-b border-[#533483]/50 flex items-center justify-between ${hasSelection ? 'bg-[#0f3460]' : 'bg-[#0f3460]/70'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`text-game text-2xl font-bold ${hasSelection ? 'text-[#ffd700]' : 'text-[#533483]'}`}>
                      {index + 1}
                    </span>
                    <span className="text-game text-lg text-white">
                      {hasSelection ? CLASS_INFO[selectedClass].name : 'Choose Class'}
                    </span>
                  </div>
                  {hasSelection && (
                    <span className="text-game text-xs text-green-400 bg-green-900/30 px-2 py-1 rounded">
                      Ready
                    </span>
                  )}
                </div>
                <div className="p-3 space-y-2">
                  {classOptions.map(({ class: classType, color }) => {
                    const isSelected = selectedClass === classType;
                    const isUsedByOther = otherSelectedClasses.includes(classType);
                    const isDisabled = isUsedByOther && !isSelected;
                    const usedBySlot = isUsedByOther ? selectedClasses.findIndex(c => c === classType) + 1 : null;

                    return (
                      <button
                        key={classType}
                        onClick={() => !isDisabled && handleClassSelect(index, classType)}
                        disabled={isDisabled}
                        className={`
                          w-full p-3 rounded-md text-left transition-all
                          ${isSelected ? `${color} ring-2 ring-white/40 shadow-lg` : ''}
                          ${isDisabled ? 'opacity-40 cursor-not-allowed bg-[#1a1a2e]' : !isSelected ? 'bg-[#0f3460] hover:bg-[#1a4a7a] hover:scale-[1.02]' : ''}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-1 rounded ${isSelected ? 'bg-white/10' : ''}`}>
                            <ClassIcon classType={classType} size={32} />
                          </div>
                          <span className={`text-game text-lg ${isSelected ? 'text-white font-bold' : ''}`}>
                            {CLASS_INFO[classType].name}
                          </span>
                          {isUsedByOther && !isSelected && (
                            <span className="text-game text-xs text-gray-500 ml-auto bg-[#0f0e17]/50 px-2 py-1 rounded">
                              Slot {usedBySlot}
                            </span>
                          )}
                          {isSelected && (
                            <span className="text-game text-sm ml-auto">✓</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

          {/* Stats panel - show selected characters only */}
          {selectedClasses.some(c => c !== null) && (
            <div className="col-span-full mt-8">
              <div className="ff-border bg-[#16213e] rounded-lg overflow-hidden">
                <div className="bg-[#0f3460] px-5 py-3 border-b border-[#533483]/50 flex items-center justify-between">
                  <span className="text-game text-lg font-bold text-[#ffd700]">Party Overview</span>
                  <span className="text-game text-sm text-gray-400">
                    {selectedClasses.filter(c => c !== null).length}/4 selected
                  </span>
                </div>
                <div className="p-4 flex flex-wrap gap-4 justify-center">
                  {selectedClasses.map((classType, index) => {
                    if (classType === null) return null;
                    const info = CLASS_INFO[classType];
                    return (
                      <div key={index} className="bg-[#0f3460] p-4 rounded-lg min-w-[180px] flex-1 max-w-[220px]">
                        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#533483]/30">
                          <span className="text-game text-lg font-bold text-[#ffd700]">{index + 1}</span>
                          <span className="text-game text-base font-bold text-[#e94560]">{info.name}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-game text-sm">
                          <div className="text-center">
                            <div className="text-gray-500 text-xs">HP</div>
                            <div className="text-[#00ff00] text-xl font-bold">{info.baseHP}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-500 text-xs">STR</div>
                            <div className="text-[#ff6644] text-xl font-bold">{info.baseStr}</div>
                          </div>
                          <div className="text-center">
                            <div className="text-gray-500 text-xs">AGI</div>
                            <div className="text-[#44ff44] text-xl font-bold">{info.baseAgi}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation modal */}
      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Ready to Begin?">
        <div className="p-6">
          <p className="text-game text-base text-gray-300 text-center mb-6">
            Your party of Warriors of Light awaits!
          </p>
          <div className="flex justify-center gap-6 mb-6">
            {selectedClasses.map((classType, index) => {
              if (classType === null) return null;
              return (
                <div key={index} className="flex flex-col items-center">
                  <ClassSprite classId={classType} size={64} />
                  <span className="text-game text-sm text-white mt-2">
                    {CLASS_INFO[classType].name}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="text-center text-game text-xs text-gray-500 mb-6">
            This will create a new game in Slot {(slotToUse ?? 0) + 1}
          </div>
          <div className="flex justify-center gap-4">
            <Button variant="secondary" size="md" onClick={() => setShowConfirm(false)}>
              Go Back
            </Button>
            <Button variant="primary" size="lg" onClick={handleCreateGame}>
              Begin Adventure!
            </Button>
          </div>
        </div>
      </Modal>

      {/* Full-screen loading overlay when creating game */}
      {isCreating && (
        <LoadingSpinner
          size="lg"
          text="Creating your adventure on the blockchain... Please confirm in your wallet."
          fullScreen
        />
      )}
    </div>
  );
};

export default NewGame;
